import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  House, Microphone, Stop, ArrowRight, CheckCircle, Shield, Sparkle,
  WhatsappLogo, ShareNetwork,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * QuantoValeCasaMia — landing pubblica SEO per la stima casa gratuita.
 *
 * Funnel:
 *   1. Form 4 campi (città, mq, stato, vani) — oppure voice
 *   2. Mostro range OMI on screen
 *   3. CTA "Stima dettagliata gratuita" → form contatto → lead salvato
 *
 * SEO: title + meta description ottimizzati. H1 chiaro. URL pulito.
 * Contenuti testuali sotto la fold per dare materiale a Google.
 */
export default function QuantoValeCasaMia({ presetCity = null } = {}) {
  const [phase, setPhase] = useState("form"); // form | result | agents | leadForm | thanks
  const [form, setForm] = useState({
    city: presetCity || "",
    surface_sqm: "",
    state: "buono",
    rooms: "",
    address: "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [contact, setContact] = useState({ name: "", phone: "", email: "", consent: false, notes: "" });

  // SEO: setta title, meta description, canonical, structured data lato
  // client. Non è SSR-puro ma Google ormai indicizza JavaScript-rendered.
  useEffect(() => {
    const cityTitle = presetCity
      ? `Quanto vale la mia casa a ${presetCity}? Stima OMI gratuita · Pronto`
      : "Quanto vale la mia casa? Stima OMI gratuita in 30 secondi · Pronto";
    const cityDesc = presetCity
      ? `Calcola gratuitamente il valore della tua casa a ${presetCity} in 30 secondi. Dati ufficiali OMI dell'Agenzia delle Entrate. Stima immediata, senza registrazione.`
      : "Calcola il valore della tua casa in 30 secondi. Stima gratuita basata sui dati ufficiali OMI dell'Agenzia delle Entrate. Senza registrazione.";
    const canonicalUrl = presetCity
      ? `https://prontoimmo.it/quanto-vale-casa-mia/${presetCity.toLowerCase()}`
      : "https://prontoimmo.it/quanto-vale-casa-mia";
    document.title = cityTitle;
    const ensure = (selector, attr, value, key, content) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(attr);
        if (key) el.setAttribute(...key);
        document.head.appendChild(el);
      }
      if (typeof content === "string") el.setAttribute("content", content);
      return el;
    };
    ensure('meta[name="description"]', "meta",
      null, ["name", "description"], cityDesc);
    ensure('meta[name="robots"]', "meta", null, ["name", "robots"], "index, follow");
    ensure('meta[property="og:title"]', "meta", null,
      ["property", "og:title"],
      presetCity
        ? `Quanto vale la tua casa a ${presetCity}? · Stima gratuita OMI`
        : "Quanto vale la mia casa? · Stima gratuita OMI"
    );
    ensure('meta[property="og:description"]', "meta", null,
      ["property", "og:description"],
      presetCity
        ? `Valore della tua casa a ${presetCity} in 30 secondi, dati ufficiali OMI. Gratis.`
        : "Valore della tua casa in 30 secondi, dati ufficiali OMI Agenzia delle Entrate. Gratis."
    );
    ensure('meta[property="og:type"]', "meta", null, ["property", "og:type"], "website");
    ensure('meta[property="og:url"]', "meta", null, ["property", "og:url"], canonicalUrl);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // JSON-LD: Service + FAQ (entrambi danno risultati arricchiti su Google)
    let ld = document.getElementById("qv-ld-json");
    if (!ld) {
      ld = document.createElement("script");
      ld.id = "qv-ld-json";
      ld.type = "application/ld+json";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": presetCity ? `Stima casa gratuita OMI ${presetCity}` : "Stima casa gratuita OMI",
        "provider": { "@type": "Organization", "name": "Pronto" },
        "description": presetCity
          ? `Valutazione gratuita immobile a ${presetCity} basata sui dati OMI ufficiali dell'Agenzia delle Entrate italiana.`
          : "Valutazione gratuita immobile basata sui dati OMI ufficiali dell'Agenzia delle Entrate italiana.",
        "areaServed": presetCity || "IT",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Come funziona la stima OMI gratuita?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "L'Osservatorio del Mercato Immobiliare (OMI) è un servizio dell'Agenzia delle Entrate che pubblica trimestralmente i prezzi medi al metro quadro per ogni comune italiano. Stima Casa usa esattamente questi dati ufficiali e li aggiusta in base a stato, piano, classe energetica e pertinenze."
            }
          },
          {
            "@type": "Question",
            "name": "Quanto è precisa la stima?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Il range che ricevi è un AVM (Automated Valuation Model) indicativo: ±10-15% sul valore reale. Per la vendita serve sempre un agente immobiliare con sopralluogo. La stima online è il punto di partenza."
            }
          },
          {
            "@type": "Question",
            "name": "Devo pagare o registrarmi?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. La stima OMI è completamente gratuita e senza registrazione. Lasci il numero solo se vuoi essere ricontattato da un agente per una valutazione dettagliata."
            }
          },
          {
            "@type": "Question",
            "name": "I miei dati sono al sicuro?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "I dati che inserisci per la stima non vengono salvati. Solo se decidi di lasciare nome e telefono per una valutazione, questi vengono inoltrati all'agente che hai scelto. Nessuno spam, nessuna lista di marketing."
            }
          },
          {
            "@type": "Question",
            "name": "Cosa influenza il valore della casa?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "I fattori principali sono: zona (centrale/semicentrale/periferica), stato di manutenzione (può cambiare il prezzo del ±25%), piano e ascensore, classe energetica (sempre più decisiva), esposizione, pertinenze come box auto o terrazza."
            }
          }
        ]
      }
    ]);

    return () => {};
  }, [presetCity]);

  const onCompute = async () => {
    if (!form.city.trim() || !form.surface_sqm) {
      toast.error("Inserisci almeno città e metri quadri.");
      return;
    }
    setBusy(true);
    try {
      // Stima senza lead: calcoliamo lato server passando dati fittizi name/phone
      // — il backend ha consent=true gating, ma in questa fase vogliamo solo
      // mostrare il range. Quindi sfruttiamo l'endpoint authenticated value
      // via un proxy "preview"? No, meglio: chiamiamo direttamente l'endpoint
      // di stima OMI senza salvare il lead. Visto che già l'endpoint richiede
      // contatto, qui chiamiamo un'API alternativa: faremo una GET di preview
      // senza salvataggio. Per ora chiamiamo il POST con flag preview=true.
      const r = await fetch(`${API}/public/quanto-vale-casa-mia/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          city: form.city.trim(),
          surface_sqm: Number(form.surface_sqm),
          state: form.state,
          rooms: form.rooms ? Number(form.rooms) : undefined,
          address: form.address || undefined,
        }),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `Errore ${r.status}`);
      }
      const data = await r.json();
      setResult(data.result);
      setPhase("result");
    } catch (err) {
      toast.error("Stima non riuscita. Riprova fra un istante.");
    } finally {
      setBusy(false);
    }
  };

  const onSubmitLead = async () => {
    if (!contact.name.trim() || !contact.phone.trim() || !contact.consent) {
      toast.error("Compila nome, telefono e dai il consenso al contatto.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/quanto-vale-casa-mia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          city: form.city.trim(),
          surface_sqm: Number(form.surface_sqm),
          state: form.state,
          rooms: form.rooms ? Number(form.rooms) : undefined,
          address: form.address || undefined,
          name: contact.name.trim(),
          phone: contact.phone.trim(),
          email: contact.email.trim() || undefined,
          consent: contact.consent,
          notes: contact.notes.trim() || undefined,
          selected_agent_user_id: selectedAgent?.id || undefined,
        }),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `Errore ${r.status}`);
      }
      setPhase("thanks");
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o chiamaci.");
    } finally {
      setBusy(false);
    }
  };

  // Carica agenti della città dopo che la stima è stata mostrata.
  const loadAgents = async () => {
    if (!form.city.trim()) return;
    setAgentsLoading(true);
    try {
      const r = await fetch(
        `${API}/public/quanto-vale-casa-mia/agents?city=${encodeURIComponent(form.city.trim())}`,
      );
      if (!r.ok) throw new Error();
      const data = await r.json();
      setAgents(data.agents || []);
    } catch (err) {
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  };

  const goToAgents = async () => {
    await loadAgents();
    setPhase("agents");
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1C1C1A] font-sans">
      {/* Top bar minimal — isolato dal sito B2B: nessun link a "Torna al
          sito" né navigation menu. Solo il brand "Pronto Stima" come
          identità della pagina. Il privato non deve scoprire il prodotto
          per agenti, altrimenti rischia di voler fare da solo. */}
      <header className="border-b border-[#EAE7DF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 font-editorial text-[19px] text-[#1C1C1A]" data-testid="qv-logo">
            <House size={18} weight="duotone" className="text-[#7C8B6E]" />
            <span>Stima Casa</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold ml-1.5 hidden sm:inline">by Pronto</span>
          </div>
          <span className="text-[10.5px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">Gratis · OMI ufficiale</span>
        </div>
      </header>

      {/* Hero + form */}
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/25 mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5A6A4D]">
            Stima gratuita · senza registrazione
          </span>
        </div>
        <h1 className="font-editorial text-[28px] sm:text-[40px] lg:text-[48px] leading-[1.1] tracking-tight">
          Quanto vale la tua casa{presetCity ? ` a ${presetCity}` : ""}?<br />
          <span className="text-[#7C8B6E] italic">In 30 secondi, senza giri di parole.</span>
        </h1>
        <p className="mt-4 text-[15px] sm:text-[17px] text-[#3F3D38] leading-relaxed max-w-2xl">
          {presetCity ? (
            <>Valutazione gratuita per immobili a <strong>{presetCity}</strong> basata sui dati ufficiali OMI dell'<strong>Agenzia delle Entrate</strong> + aggiustamenti automatici per stato, piano e classe energetica.</>
          ) : (
            <>Valutazione basata sui dati ufficiali OMI dell'<strong>Agenzia delle Entrate</strong> + aggiustamenti automatici per stato, piano e classe energetica.</>
          )}
          {" "}Nessuna registrazione. Nessuna pubblicità mascherata da consulenza.
        </p>

        {/* FORM card */}
        {phase === "form" && (
          <FormCard
            form={form} setForm={setForm}
            onSubmit={onCompute} busy={busy}
            onVoice={(fields) => {
              setForm((f) => ({
                ...f,
                city: fields.city || f.city,
                surface_sqm: fields.surface_sqm != null ? String(fields.surface_sqm) : f.surface_sqm,
                state: fields.state || f.state,
                rooms: fields.rooms != null ? String(fields.rooms) : f.rooms,
                address: fields.address || f.address,
              }));
              toast.success("Dati estratti dal tuo vocale. Controlla e calcola.");
            }}
          />
        )}

        {phase === "result" && (
          <ResultCard
            result={result}
            form={form}
            onContinue={goToAgents}
            onRetry={() => setPhase("form")}
          />
        )}

        {phase === "agents" && (
          <AgentsCard
            agents={agents}
            loading={agentsLoading}
            city={form.city}
            onSelect={(a) => {
              setSelectedAgent(a);
              setPhase("leadForm");
            }}
            onSkip={() => {
              setSelectedAgent(null);
              setPhase("leadForm");
            }}
            onBack={() => setPhase("result")}
          />
        )}

        {phase === "leadForm" && (
          <LeadFormCard
            contact={contact} setContact={setContact}
            onSubmit={onSubmitLead} busy={busy}
            onBack={() => setPhase("agents")}
            selectedAgent={selectedAgent}
          />
        )}

        {phase === "thanks" && <ThanksCard contact={contact} selectedAgent={selectedAgent} />}

        {/* SEO content block */}
        <div className="mt-16 prose prose-stone max-w-none">
          <h2 className="font-editorial text-[22px] sm:text-[28px] mt-12 mb-3 text-[#1C1C1A]">
            Come funziona la stima casa OMI gratuita
          </h2>
          <p className="text-[14.5px] text-[#3F3D38] leading-relaxed">
            L'<strong>Osservatorio del Mercato Immobiliare (OMI)</strong> è un servizio
            dell'Agenzia delle Entrate che pubblica trimestralmente i prezzi medi
            al metro quadro per ogni comune e microzona italiana. Pronto usa
            esattamente questi dati ufficiali — non stime inventate — e li
            aggiusta in base allo stato di manutenzione, al piano, alla classe
            energetica e alla presenza di pertinenze (garage, balcone, terrazzo).
          </p>
          <h2 className="font-editorial text-[22px] sm:text-[28px] mt-10 mb-3 text-[#1C1C1A]">
            Quanto è precisa?
          </h2>
          <p className="text-[14.5px] text-[#3F3D38] leading-relaxed">
            Il range che ricevi è un <strong>AVM (Automated Valuation Model)</strong>:
            indicativo, non sostituisce una perizia tecnica giurata. Per la
            vendita ti consigliamo sempre un sopralluogo con un agente immobiliare
            locale. Se vuoi, dopo la stima puoi lasciare il tuo numero: un agente
            Pronto della tua zona ti chiamerà entro 24 ore per una valutazione
            personalizzata gratuita e senza impegno.
          </p>
          <h2 className="font-editorial text-[22px] sm:text-[28px] mt-10 mb-3 text-[#1C1C1A]">
            Cosa influenza il prezzo della tua casa?
          </h2>
          <ul className="text-[14.5px] text-[#3F3D38] leading-relaxed space-y-1.5">
            <li><strong>Zona OMI</strong>: centrale, semicentrale, periferica</li>
            <li><strong>Stato</strong>: ottimo / buono / da ristrutturare può cambiare il prezzo del ±25%</li>
            <li><strong>Piano</strong>: piano nobile vs piano terra senza ascensore</li>
            <li><strong>Classe energetica</strong>: dal 2024 conta molto per acquirenti e mutuatari</li>
            <li><strong>Esposizione e luminosità</strong>: angolari e vista valgono il 5-10% in più</li>
            <li><strong>Pertinenze</strong>: box auto vale €15-40k a Milano, €5-15k in provincia</li>
          </ul>

          {/* FAQ accordion — visibili nella pagina + indicizzate da Google
              tramite il FAQPage JSON-LD impostato nello useEffect */}
          <h2 className="font-editorial text-[22px] sm:text-[28px] mt-12 mb-4 text-[#1C1C1A]">
            Domande frequenti
          </h2>
          <div className="space-y-3">
            {[
              {
                q: "Devo pagare o registrarmi?",
                a: "No. La stima OMI è completamente gratuita e senza registrazione. Lasci il numero solo se vuoi essere ricontattato da un agente per una valutazione dettagliata."
              },
              {
                q: "I miei dati sono al sicuro?",
                a: "Sì. I dati che inserisci per la stima non vengono salvati. Solo se decidi di lasciare nome e telefono per una valutazione, questi vengono inoltrati all'agente che hai scelto. Nessuno spam, nessuna lista di marketing."
              },
              {
                q: "Cosa succede dopo che ho lasciato il numero?",
                a: "L'agente che hai scelto ti chiamerà entro 24 ore. Tu decidi se incontrarlo, sentirlo al telefono, o ignorarlo. Nessun obbligo, nessun contratto."
              },
              {
                q: "Quanto è precisa la stima OMI?",
                a: "Il range che ricevi è indicativo: ±10-15% sul valore reale. Per la vendita serve sempre un agente immobiliare con sopralluogo per il prezzo definitivo. La stima online è il punto di partenza."
              },
              {
                q: presetCity ? `Posso usarla per immobili fuori ${presetCity}?` : "Funziona per tutti i comuni italiani?",
                a: "Sì. La banca dati OMI copre tutti i comuni italiani, con prezzi aggiornati ogni trimestre. Per le città principali la precisione è massima."
              },
            ].map((f, i) => (
              <details key={i} className="bg-white border border-[#EAE7DF] rounded-lg p-4 group" data-testid={`qv-faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-[14.5px] text-[#1C1C1A] list-none flex items-center justify-between gap-2">
                  <span>{f.q}</span>
                  <span className="text-[#7C8B6E] text-[18px] transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-[13.5px] text-[#3F3D38] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>

          {/* Pagine collegate per altre città — interlink interno è gold per SEO */}
          {presetCity && (
            <div className="mt-12 pt-8 border-t border-[#EAE7DF]">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A65] font-semibold mb-3">Stima casa in altre città</div>
              <div className="flex flex-wrap gap-2">
                {["Milano","Roma","Torino","Bergamo","Bologna","Firenze","Napoli","Genova","Brescia","Verona","Padova","Bari"]
                  .filter((c) => c.toLowerCase() !== presetCity.toLowerCase())
                  .slice(0, 8)
                  .map((c) => (
                    <a
                      key={c}
                      href={`/quanto-vale-casa-mia/${c.toLowerCase()}`}
                      className="px-3 py-1.5 rounded-full bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] text-[12px] text-[#3F3D38] transition-colors"
                      data-testid={`qv-city-link-${c.toLowerCase()}`}
                    >
                      Stima casa {c}
                    </a>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Trust strip */}
        <div className="mt-12 pt-8 border-t border-[#EAE7DF] grid grid-cols-1 sm:grid-cols-3 gap-4 text-[12.5px] text-[#6B6A65]">
          <div className="flex items-center gap-2"><Shield size={16} weight="duotone" className="text-[#7C8B6E]" /> Dati OMI ufficiali Agenzia delle Entrate</div>
          <div className="flex items-center gap-2"><CheckCircle size={16} weight="duotone" className="text-[#7C8B6E]" /> Gratis, senza obbligo di vendita</div>
          <div className="flex items-center gap-2"><Sparkle size={16} weight="duotone" className="text-[#7C8B6E]" /> Pronto in 30 secondi</div>
        </div>

        {/* iter v29 r255 — Mini banner B2B per riconvertire chi è capitato qui
            ma in realtà è del nostro target (agente immobiliare). Aggiunto
            dopo che un agente confuso ha chiesto via email se Pronto fosse
            per i proprietari. */}
        <div
          className="mt-10 rounded-lg bg-[#F1F4ED] border border-[#D5DAC9] p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          data-testid="b2b-agent-banner"
        >
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-[#5A6A4D] mb-1">
              Sei un agente immobiliare?
            </div>
            <div className="text-[14px] sm:text-[15px] text-[#1C1C1A] leading-snug">
              Questa stima è uno dei tanti strumenti che <strong>Pronto</strong>{" "}
              mette in mano agli agenti: sopralluogo vocale, foto migliorate,
              agenda, vetrina, lead in arrivo dal tuo territorio. Scopri come
              lavora insieme a te.
            </div>
          </div>
          <a
            href="/"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#7C8B6E] text-white text-[13.5px] font-semibold hover:bg-[#6B7A5E] transition-colors whitespace-nowrap"
            data-testid="b2b-agent-banner-cta"
          >
            Scopri Pronto <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </main>

      <footer className="border-t border-[#EAE7DF] bg-white mt-16 py-8">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center text-[12px] text-[#6B6A65]">
          © {new Date().getFullYear()} Stima Casa · servizio gratuito di valutazione immobili in Italia · contatti: supporto@prontoimmo.it
        </div>
      </footer>
    </div>
  );
}

// ─── Form card ──────────────────────────────────────────────────────
function FormCard({ form, setForm, onSubmit, busy, onVoice }) {
  return (
    <div className="mt-10 bg-white border border-[#EAE7DF] rounded-2xl p-5 sm:p-7 shadow-[0_2px_24px_rgba(28,28,26,0.04)]" data-testid="qv-form-card">
      <VoiceCapture onResult={onVoice} />

      <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-[#9A968B]">
        <div className="flex-1 h-px bg-[#EAE7DF]" />
        oppure inserisci a mano
        <div className="flex-1 h-px bg-[#EAE7DF]" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <Field label="Città *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} placeholder="Es. Milano, Bergamo, Roma" data-testid="qv-city" />
        <Field label="Indirizzo o zona" value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="Es. Via Solferino, Brera" data-testid="qv-address" />
        <Field label="Metri quadri *" value={form.surface_sqm} onChange={(v) => setForm({ ...form, surface_sqm: v })} placeholder="Es. 95" type="number" data-testid="qv-surface" />
        <Field label="Vani / locali" value={form.rooms} onChange={(v) => setForm({ ...form, rooms: v })} placeholder="Es. 3" type="number" data-testid="qv-rooms" />
        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] mb-1.5 font-semibold">Stato</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "ottimo", l: "Ottimo / nuovo" },
              { v: "buono", l: "Buono / da rinfrescare" },
              { v: "da_ristrutturare", l: "Da ristrutturare" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setForm({ ...form, state: opt.v })}
                className={`px-3 py-2 text-[12.5px] font-medium rounded-md border transition-all ${
                  form.state === opt.v
                    ? "border-[#1C1C1A] bg-[#1C1C1A] text-white shadow-sm"
                    : "border-[#EAE7DF] bg-white text-[#3F3D38] hover:border-[#7C8B6E]"
                }`}
                data-testid={`qv-state-${opt.v}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={onSubmit}
        disabled={busy}
        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#7C8B6E] hover:bg-[#5A6A4D] text-white text-[14px] font-semibold transition-colors disabled:opacity-60"
        data-testid="qv-submit"
      >
        {busy ? "Calcolo in corso…" : "Calcola subito"}
        <ArrowRight size={16} weight="bold" />
      </button>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", ...rest }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] mb-1.5 font-semibold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3.5 py-2.5 text-[14px] border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#7C8B6E] focus:ring-2 focus:ring-[#7C8B6E]/20 bg-white"
        {...rest}
      />
    </div>
  );
}

// ─── Voice capture ──────────────────────────────────────────────────
function VoiceCapture({ onResult }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1500) {
          toast.error("Vocale troppo corto. Parla per almeno 5 secondi.");
          return;
        }
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "qv.webm");
          const r = await fetch(`${API}/public/quanto-vale-casa-mia/voice`, { method: "POST", body: fd });
          if (!r.ok) throw new Error(await r.text());
          const data = await r.json();
          onResult(data.fields || {});
        } catch (err) {
          toast.error("Trascrizione non riuscita. Riprova o inserisci a mano.");
        } finally {
          setProcessing(false);
        }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error("Microfono non disponibile. Inserisci a mano.");
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      recRef.current.stop();
    }
    setRecording(false);
  };

  return (
    <div className="text-center" data-testid="qv-voice-capture">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#5A6A4D] font-semibold mb-2 inline-flex items-center gap-1.5">
        <Sparkle size={11} weight="fill" className="text-[#7C8B6E]" />
        Modo veloce: dimmi tutto a voce
      </div>
      <div className="mt-1">
        {!recording && !processing && (
          <button
            type="button"
            onClick={start}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#7C8B6E] hover:bg-[#5A6A4D] text-white text-[13.5px] font-semibold shadow-sm transition-all"
            data-testid="qv-voice-start"
          >
            <Microphone size={16} weight="fill" /> Premi e parla
          </button>
        )}
        {recording && (
          <button
            type="button"
            onClick={stop}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#E11D48] hover:bg-[#BE123C] text-white text-[13.5px] font-semibold shadow-sm transition-all animate-pulse"
            data-testid="qv-voice-stop"
          >
            <Stop size={16} weight="fill" /> Ho finito · invia
          </button>
        )}
        {processing && (
          <div className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#EAE7DF] text-[#3F3D38] text-[13.5px] font-medium">
            <div className="w-3.5 h-3.5 rounded-full border-2 border-[#7C8B6E] border-t-transparent animate-spin" />
            Sto ascoltando…
          </div>
        )}
      </div>
      <p className="mt-2 text-[11.5px] text-[#9A968B] leading-snug max-w-md mx-auto">
        Es: <em>"Sono a Milano, Brera, ho un trilocale di 95 metri quadri al terzo piano, stato buono, classe C, con balcone."</em>
      </p>
    </div>
  );
}

// ─── Result card ────────────────────────────────────────────────────
function ResultCard({ result, form, onContinue, onRetry }) {
  const fmt = (n) => Number(n || 0).toLocaleString("it-IT");
  // iter 307: range allargato visivamente (±5% in più sul min, ±5% in più sul max)
  // → 1) preserva il valore della consulenza dell'agente
  //   2) onesto: l'AVM è indicativo, il numero preciso lo dà il sopralluogo.
  const widenedMin = Math.round(result.range_min * 0.95);
  const widenedMax = Math.round(result.range_max * 1.05);
  return (
    <div className="mt-10 bg-white border border-[#EAE7DF] rounded-2xl p-6 sm:p-8 shadow-[0_2px_24px_rgba(28,28,26,0.04)]" data-testid="qv-result-card">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#5A6A4D] mb-2">
        Stima indicativa OMI · {form.city}
      </div>
      <div className="font-editorial text-[34px] sm:text-[52px] leading-none text-[#1C1C1A] tabular-nums tracking-tight">
        €{fmt(widenedMin)} – €{fmt(widenedMax)}
      </div>
      <div className="mt-2 text-[13.5px] text-[#6B6A65] leading-snug">
        Range indicativo per immobili come il tuo nella tua zona — basato sui dati ufficiali OMI dell'Agenzia delle Entrate.
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 text-[12.5px] text-[#3F3D38]">
        <Stat label="Superficie" value={`${form.surface_sqm} m²`} />
        <Stat label="Stato" value={prettyState(form.state)} />
        <Stat label="Affidabilità" value={prettyConfidence(result.confidence)} />
      </div>

      <div className="mt-7 p-5 rounded-xl bg-[#F1F4ED] border border-[#7C8B6E]/25">
        <div className="text-[14.5px] font-semibold text-[#1C1C1A] mb-2 leading-snug">
          Vuoi sapere quanto vale <em>davvero</em> la tua casa?
        </div>
        <div className="text-[13px] text-[#3F3D38] leading-relaxed mb-4">
          Il valore preciso dipende da dettagli che il range OMI non vede: classe energetica esatta, esposizione, prospettive del condominio, stato delle finiture.
          Un <strong>agente immobiliare della tua zona</strong> ti porta una valutazione personalizzata, gratuita e senza impegno.
        </div>
        <button
          onClick={onContinue}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[13.5px] font-semibold transition-colors"
          data-testid="qv-request-lead"
        >
          Scegli un agente della tua zona <ArrowRight size={14} weight="bold" />
        </button>
      </div>

      <button
        onClick={onRetry}
        className="mt-4 text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] underline underline-offset-2"
        data-testid="qv-retry"
      >
        ← Modifica i dati
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 border-[#7C8B6E] pl-3">
      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">{label}</div>
      <div className="font-editorial text-[18px] text-[#1C1C1A] mt-0.5 leading-tight">{value}</div>
    </div>
  );
}

function prettyState(s) {
  return { ottimo: "Ottimo", buono: "Buono", da_ristrutturare: "Da ristrutturare" }[s] || s;
}

function prettyConfidence(c) {
  return { alta: "Alta", media: "Media", bassa: "Bassa" }[c] || (c ? String(c) : "Media");
}

// ─── Agents grid ────────────────────────────────────────────────────
function AgentsCard({ agents, loading, city, onSelect, onSkip, onBack }) {
  return (
    <div className="mt-10" data-testid="qv-agents-card">
      <div className="text-center mb-6">
        <h2 className="font-editorial text-[26px] sm:text-[32px] text-[#1C1C1A] leading-tight">
          Scegli l'agente Pronto<br />
          <span className="italic text-[#7C8B6E]">della tua zona.</span>
        </h2>
        <p className="mt-2 text-[13.5px] sm:text-[14.5px] text-[#6B6A65] leading-snug max-w-md mx-auto">
          Sono agenti immobiliari professionisti iscritti su Pronto, attivi in <strong>{city}</strong>.
          Scegli tu chi contattare — Pronto ti mette in collegamento e basta.
        </p>
      </div>

      {loading && (
        <div className="text-center py-10">
          <div className="inline-block w-8 h-8 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" />
        </div>
      )}

      {!loading && agents.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {agents.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a)}
              className="text-left bg-white border border-[#EAE7DF] rounded-xl p-4 sm:p-5 hover:border-[#7C8B6E] hover:shadow-md transition-all group"
              data-testid={`qv-agent-${a.id}`}
            >
              <div className="flex items-start gap-3">
                {a.picture ? (
                  <img src={a.picture} alt={a.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#F1F4ED] flex items-center justify-center flex-shrink-0">
                    <House size={20} weight="duotone" className="text-[#7C8B6E]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-editorial text-[16.5px] text-[#1C1C1A] leading-tight truncate">{a.name}</div>
                  {a.agency_name && (
                    <div className="text-[12.5px] text-[#6B6A65] truncate mt-0.5">{a.agency_name}</div>
                  )}
                  {a.agency_address && (
                    <div className="text-[11.5px] text-[#9A968B] truncate mt-0.5">{a.agency_address}</div>
                  )}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#EAE7DF] flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#6B6A65] flex-wrap">
                  {a.avg_stars ? (
                    <span className="inline-flex items-center gap-0.5 text-[#1C1C1A] font-semibold">
                      <span className="text-[#D97706] text-[13px]">★</span>
                      <span>{a.avg_stars}</span>
                      <span className="text-[#9A968B] font-normal ml-0.5">({a.reviews_count})</span>
                    </span>
                  ) : (
                    <span className="text-[#9A968B]">Nuovo su Pronto</span>
                  )}
                  {a.listings_count > 0 && (
                    <>
                      <span className="text-[#C8C5BD]">·</span>
                      <span>{a.listings_count} immobili</span>
                    </>
                  )}
                </div>
                <span className="text-[12px] font-semibold text-[#7C8B6E] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Scegli <ArrowRight size={12} weight="bold" />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading && agents.length === 0 && (
        <div className="bg-white border border-[#EAE7DF] rounded-xl p-6 text-center" data-testid="qv-agents-empty">
          <div className="text-[14.5px] text-[#3F3D38] leading-relaxed">
            Al momento non ci sono ancora agenti Pronto attivi in <strong>{city}</strong>.
          </div>
          <div className="mt-2 text-[12.5px] text-[#6B6A65]">
            Lasciaci comunque i tuoi dati: appena un agente della tua zona si iscrive ti facciamo sapere, oppure ti gestisce direttamente il team Pronto.
          </div>
          <button
            onClick={onSkip}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[13.5px] font-semibold transition-colors"
            data-testid="qv-agents-skip"
          >
            Lascia i tuoi dati <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      )}

      <button
        onClick={onBack}
        className="mt-5 text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] underline underline-offset-2"
        data-testid="qv-agents-back"
      >
        ← Torna alla stima
      </button>
    </div>
  );
}

// ─── Lead form ──────────────────────────────────────────────────────
function LeadFormCard({ contact, setContact, onSubmit, busy, onBack, selectedAgent }) {
  return (
    <div className="mt-10 bg-white border border-[#EAE7DF] rounded-2xl p-6 sm:p-7 shadow-[0_2px_24px_rgba(28,28,26,0.04)]" data-testid="qv-lead-form">
      <h2 className="font-editorial text-[22px] text-[#1C1C1A] leading-tight">
        Dimmi come ricontattarti.
      </h2>
      {selectedAgent ? (
        <p className="mt-1.5 text-[13.5px] text-[#6B6A65]">
          La tua richiesta arriverà a <strong className="text-[#1C1C1A]">{selectedAgent.name}</strong>{selectedAgent.agency_name ? ` (${selectedAgent.agency_name})` : ""}, che ti chiama entro 24h.
        </p>
      ) : (
        <p className="mt-1.5 text-[13.5px] text-[#6B6A65]">
          Nessun agente della tua zona è ancora su Pronto: ti ricontattiamo noi entro 24h.
        </p>
      )}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nome e cognome *" value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} placeholder="Es. Luca Ferrari" data-testid="qv-name" />
        <Field label="Telefono *" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} placeholder="+39 333 000 0000" type="tel" data-testid="qv-phone" />
        <div className="sm:col-span-2">
          <Field label="Email (opzionale)" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} placeholder="ti@email.it" type="email" data-testid="qv-email" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] mb-1.5 font-semibold">Note (opzionale)</label>
          <textarea
            value={contact.notes}
            onChange={(e) => setContact({ ...contact, notes: e.target.value })}
            placeholder="Tempi di vendita, motivazione, dettagli aggiuntivi…"
            rows={3}
            className="w-full px-3.5 py-2.5 text-[14px] border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#7C8B6E] focus:ring-2 focus:ring-[#7C8B6E]/20 bg-white"
            data-testid="qv-notes"
          />
        </div>
      </div>
      <label className="mt-5 flex items-start gap-3 text-[12.5px] text-[#3F3D38] leading-snug cursor-pointer">
        <input
          type="checkbox"
          checked={contact.consent}
          onChange={(e) => setContact({ ...contact, consent: e.target.checked })}
          className="mt-0.5 w-4 h-4 accent-[#7C8B6E]"
          data-testid="qv-consent"
        />
        <span>
          Acconsento al trattamento dei miei dati per essere ricontattato per la valutazione.
          <span className="text-[#9A968B] ml-1">I tuoi dati restano riservati e non saranno mai usati per spam.</span>
        </span>
      </label>
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          onClick={onSubmit}
          disabled={busy || !contact.consent}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#7C8B6E] hover:bg-[#5A6A4D] text-white text-[14px] font-semibold transition-colors disabled:opacity-60"
          data-testid="qv-lead-submit"
        >
          {busy ? "Invio in corso…" : "Invia richiesta"}
          <ArrowRight size={16} weight="bold" />
        </button>
        <button
          onClick={onBack}
          className="text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] underline underline-offset-2"
          data-testid="qv-lead-back"
        >
          ← Cambia agente
        </button>
      </div>
    </div>
  );
}

// ─── Thanks + sharing virale ────────────────────────────────────────
function ThanksCard({ contact, selectedAgent }) {
  const shareUrl = "https://prontoimmo.it/quanto-vale-casa-mia";
  const waText = encodeURIComponent(
    `Ti consiglio Stima Casa: ho calcolato gratuitamente il valore della mia casa in 30 secondi con i dati ufficiali OMI dell'Agenzia delle Entrate. Provalo: ${shareUrl}`,
  );
  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Stima Casa — valore gratuito",
          text: "Calcola gratuitamente il valore della tua casa in 30 secondi · dati ufficiali OMI",
          url: shareUrl,
        });
        return;
      }
    } catch (e) {
      /* utente ha annullato: silente */
    }
    // Fallback desktop
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiato — incollalo a chi vuoi!");
    } catch {
      toast.error("Copia manualmente: " + shareUrl);
    }
  };

  return (
    <div className="mt-10 bg-white border border-[#7C8B6E]/30 rounded-2xl p-7 sm:p-9 text-center shadow-[0_2px_24px_rgba(28,28,26,0.04)]" data-testid="qv-thanks">
      <CheckCircle size={36} weight="duotone" className="text-[#7C8B6E] mx-auto" />
      <h2 className="font-editorial text-[24px] text-[#1C1C1A] mt-3 leading-tight">
        Richiesta inviata, {contact.name.split(" ")[0]}.
      </h2>
      {selectedAgent ? (
        <p className="mt-2 text-[14px] text-[#3F3D38] leading-relaxed max-w-md mx-auto">
          <strong>{selectedAgent.name}</strong>{selectedAgent.agency_name ? ` di ${selectedAgent.agency_name}` : ""} ti chiamerà al <strong>{contact.phone}</strong> entro 24 ore.
        </p>
      ) : (
        <p className="mt-2 text-[14px] text-[#3F3D38] leading-relaxed max-w-md mx-auto">
          Ti ricontatteremo al <strong>{contact.phone}</strong> entro 24 ore.
        </p>
      )}

      {/* CTA virale: condividi con un amico — è la chiave per crescere senza budget */}
      <div className="mt-7 pt-6 border-t border-[#EAE7DF]">
        <div className="text-[12px] uppercase tracking-[0.18em] font-semibold text-[#5A6A4D] mb-3">
          Conosci qualcuno che vuole vendere?
        </div>
        <p className="text-[13.5px] text-[#3F3D38] leading-relaxed max-w-md mx-auto mb-5">
          Stima Casa è gratuito per tutti. Manda il link a un amico, un parente, un collega — fai un favore in 1 secondo.
        </p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <a
            href={`https://wa.me/?text=${waText}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#25D366] hover:bg-[#1FBC58] text-white text-[13px] font-semibold transition-colors"
            data-testid="qv-share-wa"
          >
            <WhatsappLogo size={15} weight="fill" /> Condividi su WhatsApp
          </a>
          <button
            onClick={handleNativeShare}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold transition-colors"
            data-testid="qv-share-native"
          >
            <ShareNetwork size={15} weight="fill" /> Condividi
          </button>
        </div>
      </div>
    </div>
  );
}
